import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { db } from '../config/firebase'
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore'
import {
  CareCase,
  CaseMember,
  InviteRequest,
  User,
  UserRole,
  UserPermissions,
  getDefaultPermissions,
  getRoleInfo,
  migrateRole,
  shouldDowngradeAdmin
} from '../types/user'
import { generateCaregiverInviteCode } from '../utils/inviteCodeGenerator'
import { getSubscriptionLimit, SUBSCRIPTION_LIMITS } from '../config/subscription'

interface CareContextType {
  // Estado actual
  currentCase: CareCase | null
  userCases: CareCase[]
  currentMembership: CaseMember | null
  caseMembers: CaseMember[]
  pendingInvites: InviteRequest[]
  loading: boolean

  // Permisos del usuario actual
  permissions: UserPermissions
  isAdmin: boolean

  // Gestión de casos
  loadUserCases: () => Promise<void>
  createCase: (caseData: Omit<CareCase, 'id' | 'adminId' | 'createdAt' | 'updatedAt'>) => Promise<string>
  switchToCase: (caseId: string) => Promise<void>
  updateCase: (caseId: string, updates: Partial<CareCase>) => Promise<void>
  deleteCase: (caseId: string) => Promise<void>

  // Gestión de miembros
  loadCaseMembers: (caseId: string) => Promise<void>
  inviteUser: (email: string, role: UserRole, message?: string) => Promise<void>
  updateMemberRole: (memberId: string, role: UserRole, customPermissions?: Partial<UserPermissions>) => Promise<void>
  removeMember: (memberId: string) => Promise<void>

  // Gestión de invitaciones
  loadPendingInvites: () => Promise<void>
  acceptInvite: (inviteId: string) => Promise<void>
  rejectInvite: (inviteId: string) => Promise<void>

  // Códigos de acceso
  generateInviteCode: (caseId: string) => Promise<string>
  joinWithCode: (inviteCode: string) => Promise<void>
  getCasePreview: (inviteCode: string) => Promise<{ name: string; profileType: string; profileInfo: any; memberCount: number } | null>

  // Utilidades
  hasPermission: (permission: keyof UserPermissions) => boolean
  canAccessFeature: (feature: string) => boolean
}

export const CareContext = createContext<CareContextType | undefined>(undefined)

export const CareProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, userProfile } = useAuth()
  const [currentCase, setCurrentCase] = useState<CareCase | null>(null)
  const [userCases, setUserCases] = useState<CareCase[]>([])
  const [currentMembership, setCurrentMembership] = useState<CaseMember | null>(null)
  const [caseMembers, setCaseMembers] = useState<CaseMember[]>([])
  const [pendingInvites, setPendingInvites] = useState<InviteRequest[]>([])
  const [loading, setLoading] = useState(false)

  // Función para migrar casos antiguos a la nueva estructura
  const migrateCaseToNewStructure = useCallback(async (caseData: any): Promise<CareCase> => {
    // Si ya tiene la nueva estructura, no hacer nada
    if (caseData.profileInfo && caseData.profileType) {
      return caseData as CareCase
    }

    // Migrar de la estructura antigua a la nueva
    const migratedCase: CareCase = {
      ...caseData,
      profileType: caseData.patientType || 'human',
      profileInfo: caseData.patientInfo || {
        name: 'Usuario migratorio',
        age: undefined,
        gender: undefined
      }
    }

    // Eliminar campos antiguos
    delete (migratedCase as any).patientType
    delete (migratedCase as any).patientInfo

    try {
      // Actualizar en Firebase
      await updateDoc(doc(db, 'care-cases', caseData.id), {
        profileType: migratedCase.profileType,
        profileInfo: migratedCase.profileInfo,
        updatedAt: serverTimestamp()
      })

      console.log(`Migrated case ${caseData.id} to new structure`)
    } catch (error) {
      console.error('Error migrating case:', error)
    }

    return migratedCase
  }, [])

  // Cargar invitaciones pendientes
  const loadPendingInvites = useCallback(async () => {
    if (!user?.uid || !user?.email) return

    try {
      // Cargar invitaciones pendientes de Firebase
      const invitesQuery = query(
        collection(db, 'invite-requests'),
        where('invitedEmail', '==', user.email),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      )

      const invitesSnapshot = await getDocs(invitesQuery)
      const invites = invitesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        expiresAt: doc.data().expiresAt?.toDate() || new Date()
      } as InviteRequest))

      setPendingInvites(invites)
    } catch (error) {
      console.error('Error loading pending invites:', error)
    }
  }, [user?.uid, user?.email])

  // Cargar casos del usuario
  const loadUserCases = useCallback(async () => {
    if (!user?.uid) return

    setLoading(true)
    try {
      // Buscar casos donde el usuario es miembro
      const membershipsQuery = query(
        collection(db, 'case-members'),
        where('userId', '==', user.uid),
        where('isActive', '==', true),
        orderBy('joinedAt', 'desc')
      )

      const membershipsSnapshot = await getDocs(membershipsQuery)
      const caseIds = membershipsSnapshot.docs.map(doc => doc.data().caseId)

      if (caseIds.length === 0) {
        setUserCases([])
        return
      }

      // Cargar los casos
      const cases: CareCase[] = []
      for (const caseId of caseIds) {
        const caseDoc = await getDoc(doc(db, 'care-cases', caseId))
        if (caseDoc.exists() && caseDoc.data().isActive) {
          const caseData = {
            id: caseDoc.id,
            ...caseDoc.data(),
            createdAt: caseDoc.data().createdAt?.toDate() || new Date(),
            updatedAt: caseDoc.data().updatedAt?.toDate() || new Date()
          }

          // Migrar el caso si es necesario
          const migratedCase = await migrateCaseToNewStructure(caseData)
          cases.push(migratedCase)
        }
      }

      setUserCases(cases)

      // Si no hay caso actual, seleccionar el primero
      if (!currentCase && cases.length > 0) {
        await switchToCase(cases[0].id)
      }

    } catch (error) {
      console.error('Error loading user cases:', error)
    } finally {
      setLoading(false)
    }
  }, [user?.uid, currentCase, migrateCaseToNewStructure])

  // Crear nuevo caso
  const createCase = useCallback(async (caseData: Omit<CareCase, 'id' | 'adminId' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    if (!user?.uid) throw new Error('User not authenticated')

    if (!user?.uid) throw new Error('User not authenticated')

    // Check subscription limits
    const maxCases = getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxCases')
    // Filter cases where the user is an admin/creator (optional, but stricter is better for limits)
    // For now, limiting total cases they belong to is safer to prevent exploits, 
    // but typically "owned cases" is the metric. 
    // Let's assume userCases contains all memberships. 
    // If we want to limit "Created" cases, we need to check adminId.
    const ownedCases = userCases.filter(c => c.adminId === user.uid)

    if (ownedCases.length >= maxCases) {
      throw new Error(`Plan limit reached: You can only create ${maxCases} case(s) on your current plan.`)
    }

    try {
      // Crear el caso
      const newCase: Omit<CareCase, 'id'> = {
        ...caseData,
        adminId: user.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
        inviteCode: generateInviteCodeString()
      }

      const caseRef = await addDoc(collection(db, 'care-cases'), {
        ...newCase,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      // Agregar al usuario como master admin del caso
      const adminMembership: Omit<CaseMember, 'id'> = {
        userId: user.uid,
        caseId: caseRef.id,
        role: 'master_admin',
        permissions: getDefaultPermissions('master_admin'),
        joinedAt: new Date(),
        invitedBy: user.uid,
        isActive: true,
        nickname: 'Creador'
      }

      await addDoc(collection(db, 'case-members'), {
        ...adminMembership,
        joinedAt: serverTimestamp()
      })

      // Recargar casos
      await loadUserCases()

      // NUEVO: Automáticamente cambiar al caso recién creado
      await switchToCase(caseRef.id)

      return caseRef.id
    } catch (error) {
      console.error('Error creating case:', error)
      throw error
    }
  }, [user?.uid, loadUserCases])

  // Cambiar a un caso específico
  const switchToCase = useCallback(async (caseId: string) => {
    if (!user?.uid) return

    try {
      // Cargar el caso
      const caseDoc = await getDoc(doc(db, 'care-cases', caseId))
      if (!caseDoc.exists()) throw new Error('Case not found')

      const caseData = {
        id: caseDoc.id,
        ...caseDoc.data(),
        createdAt: caseDoc.data().createdAt?.toDate() || new Date(),
        updatedAt: caseDoc.data().updatedAt?.toDate() || new Date()
      }

      // Migrar el caso si es necesario
      const migratedCase = await migrateCaseToNewStructure(caseData)

      // Cargar la membresía del usuario
      const membershipQuery = query(
        collection(db, 'case-members'),
        where('userId', '==', user.uid),
        where('caseId', '==', caseId),
        where('isActive', '==', true)
      )

      const membershipSnapshot = await getDocs(membershipQuery)
      if (membershipSnapshot.empty) throw new Error('User is not a member of this case')

      const membershipDoc = membershipSnapshot.docs[0]
      const membershipData: CaseMember = {
        id: membershipDoc.id,
        userId: membershipDoc.data().userId,
        caseId: membershipDoc.data().caseId,
        role: membershipDoc.data().role,
        permissions: membershipDoc.data().permissions,
        joinedAt: membershipDoc.data().joinedAt?.toDate() || new Date(),
        invitedBy: membershipDoc.data().invitedBy,
        isActive: membershipDoc.data().isActive,
        nickname: membershipDoc.data().nickname,
        notes: membershipDoc.data().notes
      }

      setCurrentCase(migratedCase)
      setCurrentMembership(membershipData)

      // Guardar en localStorage para persistencia
      localStorage.setItem('currentCaseId', caseId)

    } catch (error) {
      console.error('Error switching to case:', error)
      throw error
    }
  }, [user?.uid, migrateCaseToNewStructure])

  // Cargar miembros de un caso
  const loadCaseMembers = useCallback(async (caseId: string) => {
    try {
      const membersQuery = query(
        collection(db, 'case-members'),
        where('caseId', '==', caseId),
        where('isActive', '==', true),
        orderBy('joinedAt', 'asc')
      )

      const membersSnapshot = await getDocs(membersQuery)
      const batch = writeBatch(db)
      let needsMigration = false

      const members = membersSnapshot.docs.map((doc, index) => {
        const data = doc.data()
        let role = data.role as UserRole
        let permissions = data.permissions

        // Migrar roles antiguos automáticamente
        const validRoles: UserRole[] = ['master_admin', 'admin', 'caregiver', 'care_recipient']
        if (!validRoles.includes(role)) {
          console.log(`🔄 Migrando rol antiguo "${role}" para usuario ${data.userId}`)

          // Para admins, el primero se convierte en master_admin
          if (data.role === 'admin') {
            role = index === 0 ? 'master_admin' : 'admin'
          } else {
            role = migrateRole(data.role)
          }

          permissions = getDefaultPermissions(role)
          needsMigration = true

          // Actualizar en Firebase
          batch.update(doc.ref, {
            role: role,
            permissions: permissions,
            migratedAt: serverTimestamp()
          })
        }

        return {
          id: doc.id,
          userId: data.userId,
          caseId: data.caseId,
          role: role,
          permissions: permissions,
          joinedAt: data.joinedAt?.toDate() || new Date(),
          invitedBy: data.invitedBy,
          isActive: data.isActive,
          nickname: data.nickname,
          notes: data.notes
        } as CaseMember
      })

      // Ejecutar migración si es necesaria
      if (needsMigration) {
        await batch.commit()
        console.log('✅ Migración de roles completada')
      }

      setCaseMembers(members)
    } catch (error) {
      console.error('Error loading case members:', error)
    }
  }, [])

  // Invitar usuario
  const inviteUser = useCallback(async (email: string, role: UserRole, message?: string) => {
    if (!user?.uid || !currentCase) throw new Error('Invalid state')
    if (!currentMembership?.permissions.canManageUsers) throw new Error('Permission denied')

    // Check subscription limits
    const maxMembers = getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxMembersPerCase')
    // Check active members count
    const activeMembers = caseMembers.filter(m => m.isActive)

    if (activeMembers.length >= maxMembers) {
      throw new Error(`Plan limit reached: This case can only have ${maxMembers} members.`)
    }

    try {
      const invite: Omit<InviteRequest, 'id'> = {
        caseId: currentCase.id,
        invitedEmail: email,
        invitedBy: user.uid,
        role,
        message,
        status: 'pending',
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 días
      }

      await addDoc(collection(db, 'invite-requests'), {
        ...invite,
        createdAt: serverTimestamp(),
        expiresAt: serverTimestamp()
      })

      // TODO: Enviar email de invitación
      console.log(`Invitation sent to ${email}`)

    } catch (error) {
      console.error('Error inviting user:', error)
      throw error
    }
  }, [user?.uid, currentCase, currentMembership])

  // Funciones de utilidad
  const generateInviteCodeString = (): string => {
    return generateCaregiverInviteCode()
  }

  const hasPermission = useCallback((permission: keyof UserPermissions): boolean => {
    const userPermissions = currentMembership?.permissions || getDefaultPermissions('caregiver')
    return userPermissions[permission] || false
  }, [currentMembership])

  const canAccessFeature = useCallback((feature: string): boolean => {
    switch (feature) {
      case 'medications':
        return hasPermission('canAddMedications') || hasPermission('canEditMedications') || hasPermission('canMarkAsTaken')
      case 'calendar':
        return hasPermission('canViewHistory')
      case 'users':
        return hasPermission('canViewUsers')
      case 'settings':
        return hasPermission('canChangeSettings')
      default:
        return true
    }
  }, [hasPermission])

  // Cargar casos al autenticarse
  useEffect(() => {
    if (user?.uid) {
      loadUserCases()
    } else {
      setUserCases([])
      setCurrentCase(null)
      setCurrentMembership(null)
      setCaseMembers([])
    }
  }, [user?.uid, loadUserCases])

  // Auto-load saved case
  useEffect(() => {
    if (userCases.length > 0 && !currentCase) {
      const savedCaseId = localStorage.getItem('currentCaseId')
      if (savedCaseId) {
        const savedCase = userCases.find(c => c.id === savedCaseId)
        if (savedCase) {
          switchToCase(savedCaseId)
        }
      }
    }
  }, [userCases, currentCase, switchToCase])

  const value: CareContextType = {
    // Estado
    currentCase,
    userCases,
    currentMembership,
    caseMembers,
    pendingInvites,
    loading,

    // Permisos computados - ensure all roles can view users
    permissions: (() => {
      const basePermissions = currentMembership?.permissions || getDefaultPermissions('caregiver')
      const finalPermissions = {
        ...basePermissions,
        canViewUsers: true // Ensure all users can view users regardless of membership
      }

      console.log('🔧 Permission calculation:', {
        hasCurrentMembership: !!currentMembership,
        membershipRole: currentMembership?.role,
        canViewUsers: finalPermissions.canViewUsers,
        canManageUsers: finalPermissions.canManageUsers,
        userId: user?.uid
      })

      return finalPermissions
    })(),
    isAdmin: currentMembership?.role === 'admin' || currentMembership?.role === 'master_admin',

    // Funciones
    loadUserCases,
    createCase,
    switchToCase,
    updateCase: async (caseId: string, updates: Partial<CareCase>) => {
      if (!user?.uid) throw new Error('User not authenticated')

      try {
        // Actualizar el caso en Firebase
        await updateDoc(doc(db, 'care-cases', caseId), {
          ...updates,
          updatedAt: serverTimestamp()
        })

        // Recargar casos
        await loadUserCases()
      } catch (error) {
        console.error('Error updating case:', error)
        throw error
      }
    },
    deleteCase: async (caseId: string) => {
      if (!user?.uid) throw new Error('User not authenticated')

      try {
        const batch = writeBatch(db)

        // 1. Eliminar medicamentos del caso
        const medicationsQuery = query(collection(db, 'care-cases', caseId, 'medications'))
        const medicationsSnapshot = await getDocs(medicationsQuery)
        medicationsSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref)
        })

        // 2. Eliminar actividades del caso
        const activitiesQuery = query(collection(db, 'care-cases', caseId, 'activities'))
        const activitiesSnapshot = await getDocs(activitiesQuery)
        activitiesSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref)
        })

        // 3. Eliminar miembros del caso
        const membersQuery = query(
          collection(db, 'case-members'),
          where('caseId', '==', caseId)
        )
        const membersSnapshot = await getDocs(membersQuery)
        membersSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref)
        })

        // 4. Eliminar invitaciones pendientes relacionadas con el caso
        const invitesQuery = query(
          collection(db, 'invite-requests'),
          where('caseId', '==', caseId)
        )
        const invitesSnapshot = await getDocs(invitesQuery)
        invitesSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref)
        })

        // 5. Eliminar el documento del caso
        batch.delete(doc(db, 'care-cases', caseId))

        // Ejecutar todas las eliminaciones en batch
        await batch.commit()

        // Si el caso eliminado era el actual, limpiar el estado
        if (currentCase?.id === caseId) {
          setCurrentCase(null)
          setCurrentMembership(null)
          setCaseMembers([])
          localStorage.removeItem('currentCaseId')
        }

        // Recargar casos
        await loadUserCases()
      } catch (error) {
        console.error('Error deleting case:', error)
        throw error
      }
    },
    loadCaseMembers,
    inviteUser,
    updateMemberRole: async (memberId: string, role: UserRole, customPermissions?: Partial<UserPermissions>) => {
      if (!user?.uid) throw new Error('User not authenticated')

      try {
        // Actualizar el miembro en Firebase
        await updateDoc(doc(db, 'case-members', memberId), {
          role,
          permissions: {
            ...currentMembership?.permissions,
            ...customPermissions
          },
          updatedAt: serverTimestamp()
        })

        // Recargar miembros
        await loadCaseMembers(currentCase?.id || '')
      } catch (error) {
        console.error('Error updating member role:', error)
        throw error
      }
    },
    removeMember: async (memberId: string) => {
      if (!user?.uid) throw new Error('User not authenticated')

      try {
        // Eliminar el miembro de Firebase
        await deleteDoc(doc(db, 'case-members', memberId))

        // Recargar miembros
        await loadCaseMembers(currentCase?.id || '')
      } catch (error) {
        console.error('Error removing member:', error)
        throw error
      }
    },
    loadPendingInvites: loadPendingInvites,
    acceptInvite: async (inviteId: string) => {
      if (!user?.uid) throw new Error('User not authenticated')

      try {
        // Actualizar la invitación en Firebase
        await updateDoc(doc(db, 'invite-requests', inviteId), {
          status: 'accepted',
          acceptedAt: serverTimestamp()
        })

        // Recargar invitaciones
        await loadPendingInvites()
      } catch (error) {
        console.error('Error accepting invite:', error)
        throw error
      }
    },
    rejectInvite: async (inviteId: string) => {
      if (!user?.uid) throw new Error('User not authenticated')

      try {
        // Actualizar la invitación en Firebase
        await updateDoc(doc(db, 'invite-requests', inviteId), {
          status: 'rejected',
          rejectedAt: serverTimestamp()
        })

        // Recargar invitaciones
        await loadPendingInvites()
      } catch (error) {
        console.error('Error rejecting invite:', error)
        throw error
      }
    },
    generateInviteCode: async (caseId: string) => {
      if (!user?.uid) throw new Error('User not authenticated')

      try {
        // Generar un nuevo código de invitación
        const inviteCode = generateInviteCodeString()

        // Actualizar el caso en Firebase
        await updateDoc(doc(db, 'care-cases', caseId), {
          inviteCode,
          updatedAt: serverTimestamp()
        })

        return inviteCode
      } catch (error) {
        console.error('Error generating invite code:', error)
        throw error
      }
    },
    joinWithCode: async (inviteCode: string) => {
      if (!user?.uid) throw new Error('User not authenticated')

      try {
        // Buscar caso por código de invitación
        const caseQuery = query(
          collection(db, 'care-cases'),
          where('inviteCode', '==', inviteCode),
          where('isActive', '==', true)
        )

        const caseSnapshot = await getDocs(caseQuery)
        if (caseSnapshot.empty) throw new Error('Invalid invite code')

        const caseDoc = caseSnapshot.docs[0]
        const caseData = caseDoc.data()

        // Verificar límites de suscripción del administrador del caso
        const adminDoc = await getDoc(doc(db, 'users', caseData.adminId))
        const adminData = adminDoc.exists() ? adminDoc.data() as User : null
        const adminPlan = adminData?.subscriptionPlan || 'free'

        const maxMembers = getSubscriptionLimit(adminPlan, 'maxMembersPerCase')

        // Contar miembros activos actuales
        const membersQuery = query(
          collection(db, 'case-members'),
          where('caseId', '==', caseDoc.id),
          where('isActive', '==', true)
        )
        const membersSnapshot = await getDocs(membersQuery)
        const currentMembersCount = membersSnapshot.size

        if (currentMembersCount >= maxMembers) {
          throw new Error(`Plan limit reached: The case owner's plan (${adminPlan}) only allows ${maxMembers} members.`)
        }

        // Verificar si el usuario ya es miembro
        const existingMemberQuery = query(
          collection(db, 'case-members'),
          where('userId', '==', user.uid),
          where('caseId', '==', caseDoc.id),
          where('isActive', '==', true)
        )

        const existingMemberSnapshot = await getDocs(existingMemberQuery)
        if (!existingMemberSnapshot.empty) {
          throw new Error('Ya eres miembro de este caso')
        }

        // Agregar al usuario como miembro
        const newMembership: Omit<CaseMember, 'id'> = {
          userId: user.uid,
          caseId: caseDoc.id,
          role: 'caregiver',
          permissions: getDefaultPermissions('caregiver'),
          joinedAt: new Date(),
          invitedBy: caseData.adminId,
          isActive: true,
          nickname: user.displayName || 'Nuevo miembro'
        }

        await addDoc(collection(db, 'case-members'), {
          ...newMembership,
          joinedAt: serverTimestamp()
        })

        // Recargar casos del usuario
        await loadUserCases()

        // Cambiar al caso recién unido
        await switchToCase(caseDoc.id)

      } catch (error) {
        console.error('Error joining with code:', error)
        throw error
      }
    },
    getCasePreview: async (inviteCode: string) => {
      try {
        // Buscar caso por código de invitación
        const caseQuery = query(
          collection(db, 'care-cases'),
          where('inviteCode', '==', inviteCode),
          where('isActive', '==', true)
        )

        const caseSnapshot = await getDocs(caseQuery)
        if (caseSnapshot.empty) return null

        const caseDoc = caseSnapshot.docs[0]
        const caseData = caseDoc.data()

        // Contar miembros del caso
        const membersQuery = query(
          collection(db, 'case-members'),
          where('caseId', '==', caseDoc.id),
          where('isActive', '==', true)
        )

        const membersSnapshot = await getDocs(membersQuery)
        const memberCount = membersSnapshot.size

        return {
          name: caseData.name,
          profileType: caseData.profileType,
          profileInfo: caseData.profileInfo,
          memberCount
        }
      } catch (error) {
        console.error('Error getting case preview:', error)
        return null
      }
    },
    hasPermission,
    canAccessFeature
  }

  return (
    <CareContext.Provider value={value}>
      {children}
    </CareContext.Provider>
  )
}

export const useCare = () => {
  const context = useContext(CareContext)
  if (context === undefined) {
    throw new Error('useCare must be used within a CareProvider')
  }
  return context
} 